import { endOfDay } from 'date-fns';

export type QueryParamParser<TValue> = {
    encode: (value: TValue) => string;
    decode: (value: string) => TValue | undefined;
};

function defineQueryParamParser<TBaseValue>(parser: QueryParamParser<TBaseValue>) {
    return <TValue extends TBaseValue>() => {
        return parser as unknown as QueryParamParser<TValue>;
    };
}

export const stringParam = defineQueryParamParser<string>({
    encode: (value) => value,
    decode: (value) => (value ? value : undefined),
});

export const numberParam = defineQueryParamParser<number>({
    encode: (value) => value.toString(),
    decode: (value) => {
        const parsed = Number.parseFloat(value);
        return Number.isNaN(parsed) ? undefined : parsed;
    },
});

export const booleanParam = defineQueryParamParser<boolean>({
    encode: (value) => value.toString(),
    decode: (value) => {
        switch (value) {
            case 'true':
                return true;
            case 'false':
                return false;
            default:
                return undefined;
        }
    },
});

export const stringArrayParam = defineQueryParamParser<string[]>({
    encode: (value) => value.map(encodeURIComponent).join(','),
    decode: (value) => {
        if (!value) {
            return undefined;
        }
        return value.split(',').map(decodeURIComponent);
    },
});

export const dateParam = defineQueryParamParser<Date | string>({
    encode: (value) => {
        const date = value instanceof Date ? value : new Date(value);
        return date.toISOString();
    },
    decode: (value) => {
        try {
            return new Date(value);
        } catch (e) {
            console.error('Failed to parse query param', e);
            return undefined;
        }
    },
});

export const endOfDayParam = defineQueryParamParser<Date | string>({
    encode: (value) => {
        const date = value instanceof Date ? value : new Date(value);
        return endOfDay(date).toISOString();
    },
    decode: (value) => {
        try {
            return endOfDay(new Date(value));
        } catch (e) {
            console.error('Failed to parse query param', e);
            return undefined;
        }
    },
});

export const dateOnlyParam = defineQueryParamParser<Date | string>({
    encode: (value) => {
        const date = value instanceof Date ? value : new Date(value);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },
    decode: (value) => {
        try {
            return new Date(value);
        } catch (e) {
            console.error('Failed to parse query param', e);
            return undefined;
        }
    },
});

export const jsonParam = defineQueryParamParser<unknown>({
    encode: (value) => JSON.stringify(value),
    decode: (value) => {
        try {
            return JSON.parse(value);
        } catch (e) {
            console.error('Failed to parse query param', e);
            return undefined;
        }
    },
});

export type QueryParamsObjectConfig<T extends object> = {
    [K in keyof T]: QueryParamParser<NonNullable<T[K]>>;
};

export const objectParam = <T extends object>(config: QueryParamsObjectConfig<T>): QueryParamParser<T> => ({
    encode: (values: T) => {
        const result: Partial<Record<keyof T, string>> = {};

        for (const key in config) {
            const parser = config[key] as QueryParamParser<unknown>;
            const value = values[key];

            if (value !== undefined && value !== null) {
                result[key] = parser.encode(value);
            }
        }

        return JSON.stringify(result);
    },
    decode: (value) => {
        try {
            const values = JSON.parse(value);
            const result: Partial<T> = {};

            for (const key in config) {
                const parser = config[key] as QueryParamParser<unknown>;

                if (values[key]) {
                    result[key] = parser.decode(values[key]) as never;
                }
            }

            return result as T;
        } catch (e) {
            console.error('Failed to parse query param', e);
            return undefined;
        }
    },
});

export const queryParamsConfig = <T extends object>(config: QueryParamsObjectConfig<T>) => config;

type EnumLike = Record<string, string>;
const enumParserMap = new Map<Record<string, string>, QueryParamParser<string>>();

export const enumParam = <T extends EnumLike>(enumValues: T): QueryParamParser<T[keyof T]> => {
    let parser = enumParserMap.get(enumValues);

    if (!parser) {
        parser = {
            encode: (value) => value,
            decode: (value) => {
                if (!Object.values(enumValues).includes(value)) {
                    return undefined;
                }
                return value;
            },
        };
        enumParserMap.set(enumValues, parser);
    }

    return parser as QueryParamParser<unknown> as QueryParamParser<T[keyof T]>;
};

export const generateQueryParamsString = <T extends object>(
    config: QueryParamsObjectConfig<T>,
    params: Partial<T>,
    urlParams?: URLSearchParams,
): string => {
    const result = new URLSearchParams(urlParams);

    for (const key of Object.keys(config) as (keyof T)[]) {
        const value = params[key];

        if (value === undefined || value === '') {
            if (result.has(key as string)) {
                result.delete(key as string);
            }
            continue;
        }

        const parser = config[key];

        const encodedValue = parser.encode(value as never);

        if (encodedValue) {
            result.set(key as string, encodedValue);
        } else {
            result.delete(key as string);
        }
    }

    return result.toString();
};
export function parseQueryParamsString<TValue extends Record<string, unknown>>(
    params: URLSearchParams,
    config: QueryParamsObjectConfig<TValue>,
): Partial<TValue>;

export function parseQueryParamsString<
    TValue extends Record<string, unknown>,
    TDefaultValues extends Partial<TValue>,
>(
    params: URLSearchParams,
    config: QueryParamsObjectConfig<TValue>,
    defaultValues: TDefaultValues,
): Partial<TValue> & TDefaultValues;

export function parseQueryParamsString<TValue extends Record<string, unknown>>(
    params: URLSearchParams,
    config: QueryParamsObjectConfig<TValue>,
    defaultValues?: Partial<TValue>,
): Partial<TValue> {
    const result = { ...defaultValues } as Partial<TValue>;

    for (const [key, parser] of Object.entries(config)) {
        const queryValue = params.get(key);

        if (queryValue === null) {
            continue;
        }

        const parsed = parser.decode(queryValue);

        if (parsed === undefined) {
            continue;
        }

        result[key as keyof TValue] = parsed;
    }

    return result;
}
